import {
    AccountInfo,
    ComputeBudgetProgram,
    Connection,
    Keypair,
    PublicKey,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import {
    ASSOCIATED_TOKEN_PROGRAM_ID,
    createAssociatedTokenAccountIdempotentInstruction,
    getAssociatedTokenAddress,
    getAssociatedTokenAddressSync,
    TOKEN_PROGRAM_ID,
} from '@solana/spl-token';
import { BondingCurve, getBuyTokenAmountFromSolAmountQuote, PUMP_PROGRAM_ID, PumpSdk } from '@pump-fun/pump-sdk';
import { AnchorProvider, BN, Program } from '@coral-xyz/anchor';
import { getZeroslotTipInstruction, sendZeroslotTransaction } from '../0slot/0slot';
import { toast } from 'react-toastify';
import { GLOBAL, PUMP_FUN_ACCOUNT, SYSTEM_PROGRAM } from '../utils/constants';
import MyWallet from './my-wallet';

export const PUMPFUN_PROGRAM_PUBKEY = new PublicKey('6EF8rrecthR5Dkzon8Nwu78hRvfCKubJ14M5uBEwF6P');
const FEE_RECIPIENT = new PublicKey('62qc2CNXwrYqQScmEdiZFFAnJR262PxWEuNQtxfafNgV');

const SELL_DISCRIMINATOR: Uint8Array = new Uint8Array([51, 230, 133, 164, 1, 127, 131, 173]);

export function getBondingCurvePDA(mint: PublicKey) {
    return PublicKey.findProgramAddressSync([Buffer.from('bonding-curve'), mint.toBuffer()], PUMPFUN_PROGRAM_PUBKEY)[0];
}

export async function getPumpSellInstructions(seller: Keypair, mint: PublicKey, amount: bigint, minSolOutput: bigint, creator: PublicKey) {
    const bondingCurve = getBondingCurvePDA(mint);
    const [associatedBondingCurve] = PublicKey.findProgramAddressSync(
        [bondingCurve.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
        ASSOCIATED_TOKEN_PROGRAM_ID,
    );

    const tokenAccount = await getAssociatedTokenAddress(mint, seller.publicKey, false);
    const [creatorVault] = await PublicKey.findProgramAddress([Buffer.from('creator-vault'), creator.toBuffer()], PUMP_PROGRAM_ID);
    const feeProgram = new PublicKey('pfeeUxB6jkeY1Hxd7CsFCAjcbHA9rWtchMGdZ6VojVZ');
    const feeConfig = PublicKey.findProgramAddressSync([Buffer.from('fee_config'), PUMP_PROGRAM_ID.toBuffer()], feeProgram)[0];
    const sellKeys = [
        { pubkey: GLOBAL, isSigner: false, isWritable: false },
        { pubkey: FEE_RECIPIENT, isSigner: false, isWritable: true },
        { pubkey: mint, isSigner: false, isWritable: false },
        { pubkey: bondingCurve, isSigner: false, isWritable: true },
        { pubkey: associatedBondingCurve, isSigner: false, isWritable: true },
        { pubkey: tokenAccount, isSigner: false, isWritable: true },
        { pubkey: seller.publicKey, isSigner: false, isWritable: true },
        { pubkey: SYSTEM_PROGRAM, isSigner: false, isWritable: false },
        { pubkey: creatorVault, isSigner: false, isWritable: true },
        { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
        { pubkey: PUMP_FUN_ACCOUNT, isSigner: false, isWritable: true },
        { pubkey: PUMP_PROGRAM_ID, isSigner: false, isWritable: false },
        { pubkey: feeConfig, isSigner: false, isWritable: false },
        { pubkey: feeProgram, isSigner: false, isWritable: false },
    ];

    const data = Buffer.alloc(8 + 8 + 8); // 24 bytes total
    data.set(SELL_DISCRIMINATOR, 0);
    data.writeBigUInt64LE(BigInt(amount), 8); // Write base_amount_in as little-endian u64
    data.writeBigUInt64LE(BigInt(minSolOutput), 16); // Write min_quote_amount_out as little-endian u64

    return new TransactionInstruction({
        keys: sellKeys,
        programId: PUMPFUN_PROGRAM_PUBKEY,
        data: data,
    });
}

export async function getBondingCurve(connection: Connection, mint: PublicKey) {
    const bondingCurve = getBondingCurvePDA(mint);
    const accountData = await connection.getAccountInfo(bondingCurve, 'processed');
    if (!accountData) throw new Error(`Bonding curve account not found for mint: ${mint.toBase58()}`);

    const sdk = new PumpSdk(connection);

    const decodedCurve = sdk.decodeBondingCurve(accountData);

    console.log(decodedCurve);

    return decodedCurve;
}

export async function getPumpfunBuyInstruction(
    mint: PublicKey,
    buyer: PublicKey,
    creator: PublicKey,
    amount: BN,
    maxSolCost: BN,
): Promise<TransactionInstruction> {
    const sdk = new PumpSdk(new Connection('https://api.mainnet-beta.solana.com')); //placeholder

    const buyInstruction = await sdk.getBuyInstructionRaw({
        user: buyer,
        mint,
        creator,
        amount,
        solAmount: maxSolCost,
        feeRecipient: FEE_RECIPIENT,
    });

    return buyInstruction;
}

export async function buyPumpfunToken(
    connection: Connection,
    mint: PublicKey,
    buyer: Keypair,
    solAmount: BN,
    zeroSlotTip: number,
    priorityLimit: number = 130_000,
    priorityFee: number = 1_000_000,
    slippage: number = 10,
) {
    const bondingCurve = await getBondingCurve(connection, mint);

    const tokenAmount = getBuyTokenAmountFromSolAmountQuote({
        inputAmount: solAmount,
        virtualSolReserves: bondingCurve.virtualSolReserves,
        virtualTokenReserves: bondingCurve.virtualTokenReserves,
    });
    const solAmountWithSlippage = solAmount.add(solAmount.mul(new BN(Math.floor(slippage * 10))).div(new BN(1000)));

    const tx = new VersionedTransaction(
        new TransactionMessage({
            payerKey: buyer.publicKey,
            recentBlockhash: (await connection.getLatestBlockhash('confirmed')).blockhash,
            instructions: [
                ComputeBudgetProgram.setComputeUnitLimit({ units: priorityLimit }),
                ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee }),
                createAssociatedTokenAccountIdempotentInstruction(buyer.publicKey, getAssociatedTokenAddressSync(mint, buyer.publicKey), buyer.publicKey, mint),
                await getPumpfunBuyInstruction(mint, buyer.publicKey, bondingCurve.creator, tokenAmount, solAmountWithSlippage),
                getZeroslotTipInstruction(buyer.publicKey, zeroSlotTip),
            ],
        }).compileToV0Message(),
    );
    tx.sign([buyer]);

    const hash = await sendZeroslotTransaction(tx);

    toast.success(`PUMPFUN buy transaction sent: ${hash}`);

    return hash;
}

export const getBondingCurvePubkey = (mintPubkey: PublicKey) =>
    PublicKey.findProgramAddressSync([Buffer.from('bonding-curve'), mintPubkey.toBuffer()], PUMPFUN_PROGRAM_PUBKEY)[0];

export const getMinSolAmount = (tokenAmount: BN, { virtualTokenReserves, virtualSolReserves }: BondingCurve, slippage = 0) => {
    const solAmount = tokenAmount.mul(virtualSolReserves).div(virtualTokenReserves.add(tokenAmount));
    return solAmount.mul(new BN(100 - slippage)).div(new BN(100));
};

export const decodeBondingCurve = (accountInfo: AccountInfo<Buffer>): BondingCurve => {
    const sdk = new PumpSdk(new Connection('https://api.mainnet-beta.solana.com'));
    return sdk.decodeBondingCurve(accountInfo);
};
